import { useMemo } from "react"

import { extractShippingViaCompanyId } from "../../../../utils/getConsolidatedValue"
import type { ProductSelectionItem } from "../../Supplier/types/types"
import type { ShipToLocationWithWarehouse } from "../types"

import { useGetPurchaseOrderShipToLocation } from "./useGetPurchaseOrderShipToLocation"

import type { Company, FreightTerms, Incoterms, Location, PricingPackage, Product } from "@/graphql/codegen/graphql"
import { useMergedIncotermsLocations } from "@/hooks/useMergedIncotermsLocations"
import { useGetAssociatedLocations } from "@/screens/Companies/hooks/useGetAssociatedLocations"
import useDeliveryTermsPerRequestedDate from "@/screens/Procurement/PurchaseOrder/hooks/useDeliveryTermsPerRequestedDate"

interface ConsolidatedData {
  freightTerms: FreightTerms | undefined
  incoterms: Incoterms | undefined
  incotermsLocationId: string | undefined
}

type UseGetStartPurchaseOrderDataReturn = Omit<ConsolidatedData, "incotermsLocationId"> & {
  shipToLocations: ShipToLocationWithWarehouse[]
  shipToLocationListFetching: boolean
  loadMore: () => void
  incotermsLocation: Location | undefined
  incotermsLocationsFetching: boolean
  isPricingFetching: boolean
  shippingViaCompanyId: string | null
}

export const useGetStartPurchaseOrderData = ({
  selectedProducts,
  selectedCompany,
  pricingPackages = [],
}: {
  selectedProducts: ProductSelectionItem[] | Product[]
  selectedCompany?: Company
  pricingPackages?: PricingPackage[]
}): UseGetStartPurchaseOrderDataReturn => {
  const { shipToLocations, shipToLocationListFetching, loadMore } = useGetPurchaseOrderShipToLocation()
  const { associatedLocationList } = useGetAssociatedLocations({
    companyId: selectedCompany?.companyId || "",
  })
  const companyLocationAssociations = selectedProducts.flatMap(
    (product) => product.company?.locationsAssociations ?? []
  )
  const { locations: mergedLocations, isFetchingLocations: incotermsLocationsFetching } =
    useMergedIncotermsLocations(companyLocationAssociations)
  const shippingViaCompanyId = extractShippingViaCompanyId(associatedLocationList)

  const { getConsolidatedDeliveryTerms, isPricingFetching } = useDeliveryTermsPerRequestedDate({ pricingPackages })

  const { freightTermsConsolidated, incotermsConsolidated, incotermsLocationIdConsolidated } = useMemo(
    () => getConsolidatedDeliveryTerms(),
    [getConsolidatedDeliveryTerms]
  )

  const incotermsLocation = useMemo(
    () => mergedLocations.find(({ locationId }) => locationId === incotermsLocationIdConsolidated),
    [mergedLocations, incotermsLocationIdConsolidated]
  )

  return {
    shipToLocations,
    shipToLocationListFetching,
    loadMore,
    isPricingFetching,
    incotermsLocationsFetching,
    incotermsLocation,
    freightTerms: freightTermsConsolidated as FreightTerms | undefined,
    incoterms: incotermsConsolidated as Incoterms | undefined,
    shippingViaCompanyId,
  }
}
